/**
 * Metadata attached to non-text reference tokens.
 *
 * This is intentionally generic so that different domains (funnels, messaging,
 * automations, etc.) can attach their own structured metadata without changing
 * the shared tokenizer.
 */
export type ReferenceTokenMeta = Record<string, unknown>;

/**
 * Generic reference token produced by the shared tokenizer.
 *
 * - `kind: 'text'` tokens are plain text segments with no metadata.
 * - Other `kind` values represent structured references (status, step, mention,
 *   variable, etc.) and may include a `meta` object with domain-specific data.
 */
export type ReferenceToken<
  Meta extends ReferenceTokenMeta = ReferenceTokenMeta,
> =
  | { kind: 'text'; value: string }
  | { kind: string; value: string; meta?: Meta };

/**
 * Reference definition describing how to interpret a `[[kind:id]]` markup
 * segment and turn it into a structured token.
 */
export interface ReferenceDefinition<
  Meta extends ReferenceTokenMeta = ReferenceTokenMeta,
> {
  /**
   * Kind handled by this definition, e.g. `"status"`, `"step"`, `"mention"`.
   */
  kind: string;
  /**
   * Resolves the raw `id` portion of the markup into a display label and
   * optional metadata. Return `null` to treat the markup as plain text.
   */
  resolve: (id: string) => { label: string; meta?: Meta } | null;
}

/**
 * Options for the shared reference highlighter. Each definition is responsible
 * for one logical kind of token.
 */
export interface ReferenceHighlighterOptions<
  Meta extends ReferenceTokenMeta = ReferenceTokenMeta,
> {
  definitions: ReferenceDefinition<Meta>[];
}

const MARKUP_REGEX = /\[\[([a-zA-Z0-9_-]+):([^\]]+)\]\]/g;

/**
 * Tokenizes `text` into reference tokens using a generic markup grammar:
 *
 *   [[kind:id]]
 *
 * For each match, the tokenizer looks for a `ReferenceDefinition` whose
 * `kind` matches the captured `kind` string. If found, it delegates to
 * `definition.resolve(id)` to produce a token; if `resolve` returns `null`,
 * or if no definition exists for that kind, the markup is preserved as
 * plain text.
 *
 * Everything outside of `[[kind:id]]` markers is returned as `text` tokens.
 *
 * @param text - Raw text containing zero or more `[[kind:id]]` segments.
 * @param options - Definitions describing how to interpret each kind.
 * @returns An ordered list of `ReferenceToken` instances.
 */
export function tokenizeReferences<
  Meta extends ReferenceTokenMeta = ReferenceTokenMeta,
>(
  text: string,
  options: ReferenceHighlighterOptions<Meta>
): ReferenceToken<Meta>[] {
  if (!text) {
    return [];
  }

  const { definitions } = options;
  const tokens: ReferenceToken<Meta>[] = [];

  MARKUP_REGEX.lastIndex = 0;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = MARKUP_REGEX.exec(text)) !== null) {
    const matchStart = match.index;
    const matchText = match[0] ?? '';

    if (matchStart > lastIndex) {
      tokens.push({
        kind: 'text',
        value: text.slice(lastIndex, matchStart),
      });
    }

    const rawKind = match[1] ?? '';
    const rawId = match[2] ?? '';

    const definition = definitions.find((def) => def.kind === rawKind);

    if (!definition) {
      tokens.push({ kind: 'text', value: matchText });
      lastIndex = matchStart + matchText.length;
      continue;
    }

    const resolved = definition.resolve(rawId);

    if (!resolved) {
      tokens.push({ kind: 'text', value: matchText });
      lastIndex = matchStart + matchText.length;
      continue;
    }

    tokens.push({
      kind: rawKind,
      value: resolved.label,
      meta: resolved.meta,
    });

    lastIndex = matchStart + matchText.length;
  }

  if (lastIndex < text.length) {
    tokens.push({
      kind: 'text',
      value: text.slice(lastIndex),
    });
  }

  return tokens;
}
